import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    altText = "altText",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.altText]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
    };
}
type Props = OwnProps;
export interface UploadCredentialsSuccessStyles {
    mainWrapper?: InjectableCss;
    buttonWrapper?: ButtonProps;
    successfulMsg?: TypographyProps;
    icon?: LazyImageProps;
}
const styles: UploadCredentialsSuccessStyles = {
    mainWrapper: {
        css: css`
            padding: 50px;
            background: #f0f4f3;
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            text-align: center;
            margin-top: 50px
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-top: 10px;
                        `,
                        css`
                            margin-top: 10px;
                        `,
                        css`
                            margin-top: 30px;
                        `,
                        css`
                            margin-top: 50px;
                        `,
                        css`
                            margin-top: 50px;
                        `,
                    ])};
        `,
    },
    buttonWrapper: {
        css: css`
            border-radius: 10px;
            padding: 0 40px;
            text-transform: capitalize;
            margin-top: 40px;
            height: 44px;
        `,
    },
    successfulMsg: {
        variant: "h1",
        color: "primary",
        size: 22,
        css: css`
            margin-bottom: 15px;
            font-weight: unset;
            font-family: LibreBaskerville;
        `,
    },
    icon: {
        css: css`
            height: 92px;
            width: 92px;
            overflow: hidden;
            border-radius: 50%;
            margin-bottom: 35px;
            img {
                height: 100%;
            }
        `,
    },
};

const UploadCredentialsSuccess = ({ fields, id }: Props) => {
    const { url } = useGetLink(fields.buttonLink);
    const onClick = () => {
        if (!url) {
            return;
        }
        window.location.href = url;
    };
    return (
        <StyledWrapper {...styles.mainWrapper}>
            <LazyImage src={fields.image} altText={fields.altText} {...styles.icon} />
            <Typography {...styles.successfulMsg}>{siteMessage("UploadCredentails_SuccessMessage")}</Typography>
            <Zone contentId={id} zoneName="GAIABannerImage" />
            <Button {...styles.buttonWrapper} onClick={onClick}>
                {fields.buttonLabel}
            </Button>
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: UploadCredentialsSuccess,
    definition: {
        group: "My Account",
        displayName: "UploadCredentialsSuccess",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.image,
                displayName: "Icon",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.altText,
                editorTemplate: "TextField",
                displayName: "AltText",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "go to home page",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
